import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { site, nav, materialsNav } from "../../data/site";

const PG_ICON = "/pg-icon.png";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [matOpen, setMatOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMatOpen(false);
  }, [location]);

  const isActive = (path) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  const showLogo = location.pathname !== "/" || scrolled;

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-neutral-200 transition-all duration-300 ${
        scrolled ? "py-2 shadow-sm" : "py-3"
      }`}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between gap-4">
          {/* Logo (hidden on the home hero until scrolled) */}
          {showLogo ? (
            <Link to="/" data-testid="header-logo" className="flex items-center shrink-0" aria-label="Premier Granite LLC home">
              <img
                src={site.logoLight}
                alt="Premier Granite LLC"
                className="hidden sm:block h-14 md:h-[66px] w-auto"
              />
              <img src={PG_ICON} alt="Premier Granite LLC" className="sm:hidden h-12 w-auto" />
            </Link>
          ) : (
            <span className="w-10 shrink-0" aria-hidden="true" />
          )}

          {/* Desktop nav */}
          <nav className="hidden xl:flex items-center gap-7" data-testid="desktop-nav">
            {nav.map((link) =>
              link.name === "Materials" ? (
                <div key={link.name} className="relative group">
                  <Link
                    to={link.path}
                    data-testid="nav-materials"
                    className={`flex items-center gap-1 text-sm font-medium transition-colors ${
                      isActive(link.path) ? "text-[#CD1422]" : "text-neutral-700 hover:text-[#CD1422]"
                    }`}
                  >
                    {link.name}
                    <ChevronDown size={14} />
                  </Link>
                  <div className="absolute left-0 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                    <ul className="w-52 bg-white border border-neutral-200 shadow-lg rounded-md py-2">
                      {materialsNav.map((m) => (
                        <li key={m.name}>
                          <Link
                            to={m.path}
                            className="block px-4 py-2.5 text-sm text-neutral-700 hover:text-[#CD1422] hover:bg-neutral-50"
                          >
                            {m.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : (
                <Link
                  key={link.name}
                  to={link.path}
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s/g, "-")}`}
                  className={`text-sm font-medium transition-colors ${
                    isActive(link.path) ? "text-[#CD1422]" : "text-neutral-700 hover:text-[#CD1422]"
                  }`}
                >
                  {link.name}
                </Link>
              )
            )}
          </nav>

          {/* CTA */}
          <div className="hidden xl:flex items-center gap-5 shrink-0">
            <a
              href={site.phoneHref}
              data-testid="header-phone"
              className="flex items-center gap-2 text-sm font-medium text-[#1A3D84] hover:text-[#CD1422] transition-colors"
            >
              <Phone size={16} />
              {site.phone}
            </a>
            <Link to="/contact" data-testid="header-estimate-cta" className="btn btn-primary !py-2.5 !px-5">
              Get a Free Estimate
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            className="xl:hidden p-2 text-[#1A3D84]"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="xl:hidden mt-3 pb-4 border-t border-neutral-200 pt-3 bg-white" data-testid="mobile-nav">
            <nav className="flex flex-col">
              {nav.map((link) =>
                link.name === "Materials" ? (
                  <div key={link.name}>
                    <button
                      onClick={() => setMatOpen(!matOpen)}
                      className="w-full flex items-center justify-between px-2 py-3 text-sm font-medium text-neutral-800"
                    >
                      {link.name}
                      <ChevronDown size={16} className={`transition-transform ${matOpen ? "rotate-180" : ""}`} />
                    </button>
                    {matOpen && (
                      <div className="pl-4 border-l border-neutral-200 ml-2">
                        <Link to="/materials" className="block px-2 py-2 text-sm text-neutral-600">All Materials</Link>
                        {materialsNav.map((m) => (
                          <Link key={m.name} to={m.path} className="block px-2 py-2 text-sm text-neutral-600 hover:text-[#CD1422]">
                            {m.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={link.name}
                    to={link.path}
                    className={`px-2 py-3 text-sm font-medium border-b border-neutral-100 ${
                      isActive(link.path) ? "text-[#CD1422]" : "text-neutral-800"
                    }`}
                  >
                    {link.name}
                  </Link>
                )
              )}
            </nav>
            <div className="mt-4 space-y-3 px-2">
              <a href={site.phoneHref} className="flex items-center justify-center gap-2 py-2 text-sm font-medium text-[#1A3D84]">
                <Phone size={16} /> {site.phone}
              </a>
              <Link to="/contact" className="btn btn-primary w-full">Get a Free Estimate</Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
